import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Facebook } from '@/assets/icons/Facebook.tsx'
import { GoogleAds } from '@/assets/icons/GoogleAds.tsx'
import { GoogleAnalytics } from '@/assets/icons/GoogleAnalytics.tsx'
import { GoogleTagManager } from '@/assets/icons/GoogleTagManager.tsx'
import { Kwai } from '@/assets/icons/Kwai.tsx'
import { Taboola } from '@/assets/icons/Taboola'
import { Tiktok } from '@/assets/icons/Tiktok.tsx'
import Divider from '@/components/shared/Divider/Divider.tsx'
import { Input } from '@/components/shared/Input/Input'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'i18next'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

interface Platform {
  id: string
  name: string
  icon: React.ReactNode
  enabled: boolean
}

const platforms: Platform[] = [
  { id: 'facebook', name: 'Facebook', icon: <Facebook />, enabled: true },
  { id: 'tiktok', name: 'TikTok', icon: <Tiktok />, enabled: true },
  { id: 'taboola', name: 'Taboola', icon: <Taboola />, enabled: true },
  { id: 'kwai', name: 'Kwai', icon: <Kwai />, enabled: true },
  { id: 'google_ads', name: 'Google Ads', icon: <GoogleAds />, enabled: true },
  {
    id: 'google_tag_manager',
    name: 'Google Tag Manager',
    icon: <GoogleTagManager />,
    enabled: false,
  },
  {
    id: 'google_analytics',
    name: 'Google Analytics',
    icon: <GoogleAnalytics />,
    enabled: false,
  },
]

interface AddPixelSheetProps {
  onAdd: (pixel: any) => void
  isLoading: boolean
}

export const formDataSchema = z
  .object({
    name: z.string().min(1, t('common:required_field')),
    pixelId: z.string().min(1, t('common:required_field')),
    apiToken: z.string().min(1, t('common:required_field')).default(''),
    triggerOnPageView: z.boolean(),
    triggerOnInitiateCheckout: z.boolean(),
    triggerOnAddPaymentInfo: z.boolean(),
    triggerOnPurchase: z.boolean(),
    triggerOnPixelGenerated: z.boolean(),
    triggerOnBoletoGenerated: z.boolean(),
    triggerOnCardApproved: z.boolean(),
  })
  .refine(
    (data) =>
      data.triggerOnPageView ||
      data.triggerOnInitiateCheckout ||
      data.triggerOnAddPaymentInfo ||
      data.triggerOnPurchase,
    {
      message: t('common:trigger_activation_error'),
      path: ['triggerActivationError'],
    },
  )

type FormData = z.infer<typeof formDataSchema>

type TriggerKey = keyof Omit<FormData, 'name' | 'pixelId' | 'apiToken'>

const baseEventMapping: Record<
  keyof Omit<FormData, 'name' | 'pixelId' | 'apiToken'>,
  string
> = {
  triggerOnPageView: 'page_view',
  triggerOnInitiateCheckout: 'initiate_checkout',
  triggerOnAddPaymentInfo: 'add_payment_info',
  triggerOnPurchase: 'purchase',
  triggerOnPixelGenerated: 'pix_generated',
  triggerOnBoletoGenerated: 'bank_slip_generated',
  triggerOnCardApproved: 'card_approved',
}

const orderedTriggerKeys: TriggerKey[] = [
  'triggerOnPageView',
  'triggerOnInitiateCheckout',
  'triggerOnAddPaymentInfo',
  'triggerOnPurchase',
  'triggerOnPixelGenerated',
  'triggerOnBoletoGenerated',
  'triggerOnCardApproved',
]

export function AddPixelSheet({ onAdd, isLoading }: AddPixelSheetProps) {
  const { t } = useTranslation('pixels')
  const [step, setStep] = useState(1)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(
    null,
  )

  // No AddPixel, todos os providers usam o mesmo padrão de eventos (Facebook).
  const currentEventMapping = baseEventMapping
  // Só ajusta copy/texto de ajuda pro Google Ads/Taboola — schema, payload e fluxo dos
  // toggles continuam idênticos pra todos os providers.
  const isGoogleAds = selectedPlatform?.id === 'google_ads'
  // Taboola: "Pixel ID" é o Account ID da conta (não um ID por pixel/produto), e o campo de
  // token é na verdade o Event Name — precisa bater exato (incl. maiúsculas/minúsculas) com o
  // nome cadastrado no Realize (painel da Taboola), senão o evento não é reconhecido.
  const isTaboola = selectedPlatform?.id === 'taboola'

  const {
    register,
    handleSubmit,
    control,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    defaultValues: {
      name: '',
      pixelId: '',
      apiToken: '',
      triggerOnPageView: true,
      triggerOnInitiateCheckout: false,
      triggerOnAddPaymentInfo: false,
      triggerOnPurchase: false,
      triggerOnPixelGenerated: false,
      triggerOnBoletoGenerated: false,
      triggerOnCardApproved: false,
    },
  })

  const onSubmit = (data: FormData) => {
    const activateEvents = orderedTriggerKeys
      .filter((key) => (key === 'triggerOnPageView' ? true : data[key]))
      .map((key) => currentEventMapping[key])

    const formattedData = {
      name: data.name,
      pixel_events: activateEvents,
      pixel_provider: selectedPlatform?.id,
      pixel_id: data.pixelId,
      is_enabled: true,
      credential: {
        key_name: 'pixel_api_token',
        key_value: data.apiToken,
      },
    }

    onAdd(formattedData)
  }

  return (
    <section className="h-full flex flex-col justify-between">
      <div className="relative">
        <div className="flex items-center justify-between px-6 my-8">
          <div className=" flex gap-4 text-lg font-medium text-zinc-100">
            {t('add_pixel')}{' '}
            <div className="bg-[#18181B] w-[42px] text-center rounded text-sm flex justify-center align-center flex-col ">
              {step} / 2
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#18181B]">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${(step / 2) * 100}%` }}
          />
        </div>
      </div>

      {step === 1 ? (
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-2">
            {platforms.map((platform) => (
              <button
                key={platform.id}
                onClick={() => {
                  if (!platform.enabled) {
                    return
                  }
                  setSelectedPlatform(platform)
                  setStep(2)
                }}
                data-cy={`select-${platform.id}-platform`}
                disabled={!platform.enabled}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border transition-all ${
                  selectedPlatform?.id === platform.id
                    ? 'border-primary bg-primary/10'
                    : platform.enabled
                    ? 'border-zinc-800 hover:border-zinc-700 bg-[#18181B]'
                    : 'border-zinc-800 bg-[#18181B] opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className={`w-6 h-6 ${
                      !platform.enabled ? 'opacity-50' : ''
                    }`}
                  >
                    {platform.icon}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      platform.enabled ? 'text-zinc-100' : 'text-zinc-500'
                    }`}
                  >
                    {platform.name}
                  </span>
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlatform?.id === platform.id
                      ? 'border-primary bg-primary'
                      : platform.enabled
                      ? 'border-zinc-700'
                      : 'border-zinc-800'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1"
        >
          <div className="flex-1 px-6 py-4 overflow-y-auto">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-zinc-100">
                {t('platform')}: {selectedPlatform?.name}
              </h2>
            </div>

            <div className="space-y-6">
              <div className="space-y-1.5">
                <label className="block text-sm text-zinc-100">
                  {t('identification_name')}
                </label>
                <Input
                  type="text"
                  className="w-full px-3 py-2 bg-transparent border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder={t('enter_identification_name')}
                  data-cy="input-pixel-name"
                  {...register('name', { required: true })}
                  helperText={errors?.name?.message}
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm text-zinc-100">Pixel ID</label>
                <Input
                  type="text"
                  className="w-full px-3 py-2 bg-transparent border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Ex: 1234567890123456"
                  {...register('pixelId', { required: true })}
                  data-cy="input-pixel-id"
                  helperText={errors?.pixelId?.message}
                />
                {isGoogleAds && (
                  <p className="text-xs text-zinc-500">
                    {t('google_ads_pixel_id_hint')}
                  </p>
                )}
                {isTaboola && (
                  <p className="text-xs text-zinc-500">
                    {t('taboola_pixel_id_hint')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-zinc-100">
                  {isGoogleAds && t('google_ads_conversion_label')}
                  {isTaboola && t('taboola_event_name_label')}
                  {!isGoogleAds && !isTaboola && t('api_token')}{' '}
                  <span className="text-zinc-500"></span>
                </label>
                <textarea
                  className="my-1.5 w-full h-[100px] px-3 py-2 bg-transparent border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
                  placeholder={
                    isGoogleAds
                      ? t('google_ads_enter_conversion_label')
                      : isTaboola
                      ? t('taboola_enter_event_name')
                      : t('enter_api_token')
                  }
                  data-cy="input-api-token"
                  {...register('apiToken')}
                />
                {isGoogleAds && (
                  <p className="text-xs text-zinc-500 -mt-1 mb-1.5">
                    {t('google_ads_conversion_label_hint')}
                  </p>
                )}
                {isTaboola && (
                  <p className="text-xs text-zinc-500 -mt-1 mb-1.5">
                    {t('taboola_event_name_hint')}
                  </p>
                )}
                <div className="flex justify-end -mt-2">
                  <span className="text-xs text-zinc-500">
                    {watch('apiToken')?.length}/2000
                  </span>
                </div>
                {errors['apiToken'] && (
                  <p className="text-error text-sm mt-2">
                    {errors['apiToken'].message}
                  </p>
                )}
              </div>
              <Divider />
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-zinc-100">
                  {t('activation_environments')}
                </h3>
                {isGoogleAds && (
                  <Alert variant="info">
                    <p className="text-sm">
                      {t('google_ads_shared_label_warning')}
                    </p>
                  </Alert>
                )}
                {isTaboola && (
                  <Alert variant="info">
                    <p className="text-sm">
                      {t('taboola_shared_event_name_warning')}
                    </p>
                  </Alert>
                )}
                <div className="space-y-4">
                  {/* <div className="flex items-center gap-3">
                    <Controller
                      name="triggerOnPageView"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            clearErrors("triggerActivationError" as any);
                          }}
                          data-cy="switch-trigger-on-page_view"
                          className="data-[state=checked]:bg-primary"
                        />
                      )}
                    />
                    <span className="text-sm">
                      {t("trigger_page_view")}{" "}
                      <Badge
                        variant="small"
                        className="text-xs font-medium whitespace-nowrap inline-block"
                      >
                        PageView
                      </Badge>
                    </span>
                  </div> */}
                  <div className="flex items-center gap-3">
                    <Controller
                      name="triggerOnInitiateCheckout"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked)
                            clearErrors('triggerActivationError' as any)
                          }}
                          data-cy="switch-trigger-on-initiate-checkout"
                          className="data-[state=checked]:bg-primary"
                        />
                      )}
                    />
                    <span className="text-sm">
                      {t('trigger_page_view')}{' '}
                      <Badge
                        variant="small"
                        className="text-xs font-medium whitespace-nowrap inline-block"
                      >
                        {currentEventMapping.triggerOnInitiateCheckout}
                      </Badge>
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Controller
                      name="triggerOnAddPaymentInfo"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked)
                            clearErrors('triggerActivationError' as any)
                          }}
                          data-cy="switch-trigger-on-add-payment-info"
                          className="data-[state=checked]:bg-primary"
                        />
                      )}
                    />
                    <span className="text-sm">
                      {t('trigger_add_payment_info')}{' '}
                      <Badge
                        variant="small"
                        className="text-xs font-medium whitespace-nowrap inline-block"
                      >
                        {currentEventMapping.triggerOnAddPaymentInfo}
                      </Badge>
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Controller
                      name="triggerOnPurchase"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked)
                            clearErrors('triggerActivationError' as any)
                          }}
                          data-cy="switch-trigger-on-purchase"
                          className="data-[state=checked]:bg-primary"
                        />
                      )}
                    />
                    <span className="text-sm">
                      {t('trigger_on_purchase')}{' '}
                      <Badge
                        variant="small"
                        className="text-xs font-medium whitespace-nowrap inline-block"
                      >
                        {currentEventMapping.triggerOnPurchase}
                      </Badge>
                    </span>
                  </div>
                </div>
              </div>
              {errors['triggerActivationError'] && (
                <p className="text-error text-sm mt-2">
                  {errors['triggerActivationError'].message}
                </p>
              )}
              {/* <Divider />
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-zinc-100">
                  {t("activation_conditions")}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Controller
                      name="triggerOnPixelGenerated"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            clearErrors("triggerConditionError" as any);
                          }}
                          className="data-[state=checked]:bg-primary"
                          data-cy="switch-trigger-on-pix"
                        />
                      )}
                    />
                    <span className="text-sm">{t("pixel_generated")}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Controller
                      name="triggerOnBoletoGenerated"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            clearErrors("triggerConditionError" as any);
                          }}
                          className="data-[state=checked]:bg-primary"
                          data-cy="switch-trigger-on-boleto"
                        />
                      )}
                    />
                    <span className="text-sm">{t("boleto_generated")}</span>
                  </div>
                </div>
              </div> */}

              {errors['triggerConditionError'] && (
                <p className="text-error text-sm mt-2">
                  {errors['triggerConditionError'].message}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end p-4 border-t max-sm:pb-12">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep(1)}
              className="text-zinc-400 hover:text-zinc-100"
            >
              {t('cancel')}
            </Button>
            <Button
              disabled={isLoading}
              type="submit"
              data-cy="btn-save-pixel"
              className="bg-primary text-black hover:bg-primary/90 px-6"
            >
              {t('save_pixel')}
            </Button>
          </div>
        </form>
      )}
    </section>
  )
}
